import { observer } from "@legendapp/state/react";
import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { fetchSession } from "@/apis/clients/sessionClient";
import { router } from "@/router";
import { AuthStore$, setSession } from "@/stores/authStore";
import { UserStore$, fetchUserInfo, setUserInfo } from "@/stores/userStore";
import { TEXT } from "@/constants/text";
import styles from "@/App.module.css";

/**
 * Asks the backend who the user is before rendering anything that depends on
 * the answer.
 *
 * Without this the router's guard reads a default of false and bounces every
 * visitor to the login page, including one who is already signed in. Rendering
 * nothing while the question is outstanding is deliberate: showing the login
 * page first and correcting it a moment later is a worse experience than a
 * brief blank.
 *
 * A failure that is not a 401 leaves isAuthenticated false and surfaces on the
 * console rather than being treated as "signed out". The user is not logged
 * out because the backend is unwell, and sending them to a login they cannot
 * complete would hide that.
 */
const App = observer(() => {
  const [sessionOutage, setSessionOutage] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSession()
      .then(async (session) => {
        if (cancelled) return;
        setSession(session);
        if (session !== null) {
          await fetchUserInfo().catch((error: unknown) => {
            if (!cancelled) {
              // eslint-disable-next-line no-console
              console.error("could not load user info", error);
              setSession(null);
              setUserInfo(null);
            }
          });
        } else {
          setUserInfo(null);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("could not determine the session", error);
          // fetchSession threw — a backend outage, not a user logout.
          // Surface an explicit error rather than routing to the login page,
          // which the user cannot complete if the auth service is down.
          setSessionOutage(true);
          setUserInfo(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (sessionOutage) {
    return <p className={styles.outage}>{TEXT.auth.sessionCheckFailed}</p>;
  }

  if (AuthStore$.isResolving.get() || UserStore$.isResolving.get()) return null;

  return <RouterProvider router={router} />;
});

export default App;
